import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Card, PageHeader, Btn, Select } from "../components/UI";
import { PromoTabs } from "../components/promo/PromoTabs";
import { FunnelBars } from "../components/promo/FunnelBars";
import { AnalyticsKpis } from "../components/promo/AnalyticsKpis";
import { AnalyticsTrend } from "../components/promo/AnalyticsTrend";
import { BreakdownTable } from "../components/promo/BreakdownTable";
import { FindYourRacePanel } from "../components/promo/FindYourRacePanel";
import { FollowupControls } from "../components/promo/FollowupControls";
import { AbuseQueue } from "../components/promo/AbuseQueue";
import { errMsg, downloadBlob } from "../components/promo/format";

const WINDOWS = [[7, "Last 7 days"], [30, "Last 30 days"], [90, "Last 90 days"], [365, "Last 12 months"]];

export default function PromoAnalytics() {
  const [days, setDays] = useState(30);
  const [promoId, setPromoId] = useState("");
  const [eventId, setEventId] = useState("");
  const [promos, setPromos] = useState([]);
  const [events, setEvents] = useState([]);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = { days, ...(promoId && { promotion_id: promoId }), ...(eventId && { event_id: eventId }) };
      const { data } = await api.get("/promotions/analytics", { params });
      setData(data);
    } catch (e) { toast.error(errMsg(e, "Could not load analytics")); } finally { setBusy(false); }
  }, [days, promoId, eventId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/promotions").then(({ data }) => setPromos(data.promotions || [])).catch(() => {});
    api.get("/promotions/events").then(({ data }) => setEvents(data.events || [])).catch(() => {});
  }, []);

  const exportCsv = async () => {
    try {
      const params = { ...(promoId && { promotion_id: promoId }), ...(eventId && { event_id: eventId }) };
      const res = await api.get("/promotions/analytics/export", { params, responseType: "blob" });
      downloadBlob(res.data, `promo-analytics-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch (e) { toast.error(errMsg(e, "Export failed")); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="promo-analytics-page">
      <PageHeader eyebrow="Admin · Promotions" title="Promo Analytics"
        subtitle="From printed card to paid launch — scans, claims, redemptions, liability and who's converting."
        action={<Btn variant="secondary" onClick={exportCsv} data-testid="analytics-export-btn"><Download className="w-4 h-4" /> Export CSV</Btn>} />
      <PromoTabs />

      <Card className="!p-4 flex items-end gap-3 flex-wrap" data-testid="analytics-filters">
        <Select label="Window" value={days} onChange={(e) => setDays(parseInt(e.target.value))} className="w-44" data-testid="analytics-days-select">
          {WINDOWS.map(([d, l]) => <option key={d} value={d}>{l}</option>)}
        </Select>
        <Select label="Promotion" value={promoId} onChange={(e) => setPromoId(e.target.value)} className="w-56" data-testid="analytics-promo-select">
          <option value="">All promotions</option>
          {promos.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
        <Select label="Trade show" value={eventId} onChange={(e) => setEventId(e.target.value)} className="w-56" data-testid="analytics-event-select">
          <option value="">All trade shows</option>
          {events.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
        </Select>
        <Btn variant="secondary" onClick={load} disabled={busy} data-testid="analytics-refresh-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </Btn>
        {data?.generated_at && <span className="text-[11px] text-slate-500 ml-auto">Updated {data.generated_at.slice(11, 16)} UTC</span>}
      </Card>

      {!data ? (
        <div className="text-sm text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Crunching numbers…</div>
      ) : (
        <>
          <AnalyticsKpis data={data} />
          <FindYourRacePanel data={data.find_your_race} reload={load} />
          <FollowupControls onSaved={load} />
          <AbuseQueue />
          <div className="grid lg:grid-cols-5 gap-4">
            <Card className="!p-5 lg:col-span-2">
              <div className="font-display font-bold text-sm mb-3">Redemption funnel</div>
              <FunnelBars stats={data.funnel} testid="analytics-funnel" />
              <div className="text-[11px] text-slate-500 mt-3">Reserved = held against a launch awaiting approval. Percentages are of live (non-void) codes.</div>
            </Card>
            <div className="lg:col-span-3"><AnalyticsTrend series={data.series} /></div>
          </div>
          <BreakdownTable title="By promotion" rows={data.by_promotion} nameKey="name" subKey="kind" testid="table-by-promotion" emptyText="No codes issued yet." />
          <BreakdownTable title="By trade show" rows={data.by_event} nameKey="name" subKey="venue" testid="table-by-event" emptyText="No trade-show codes yet — build one under Trade Shows." />
          <BreakdownTable title="By booth staff" rows={data.by_staff} nameKey="staff_name" subKey="event_name" testid="table-by-staff" emptyText="No staff-attributed codes yet — hand codes to staff inside a trade show." />
        </>
      )}
    </div>
  );
}
